import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { apiFetch, isNotAvailable } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { LoadingState, ErrorState, NotAvailableState } from "../components/States.js";
import { Chart } from "../components/Chart.js";
import { KpiCard } from "../components/KpiCard.js";
import { TanTableViz } from "../components/TanTableViz.js";
import { AiContentRenderer } from "../components/AiContentRenderer.js";
import { AiProductionPromptPanel } from "../components/AiProductionPromptPanel.js";
import { PageActiveFilterChips } from "../components/PageActiveFilterChips.js";
import { PageFilterDropdown } from "../components/PageFilterDropdown.js";
import { PageFilterDateRange } from "../components/PageFilterDateRange.js";
import { useToast } from "../components/Toast.js";
import {
  applyTanTableRowSelectToSelections,
  distinctValues,
  effectiveFilterSelectionsForRender,
  filterRenderModel,
  filterSourceKey,
  isDateRangeSlicer,
  isPageSlicerFilter,
  slicerNotAvailable,
  resolveEchartsOption,
  type PageFilter,
  type PageFilterRenderModel as RenderModel,
  type TanTableRowSelectEvent
} from "../lib/pageFilters.js";

function isInteractionFilter(f: PageFilter): boolean {
  return (f.filter_kind ?? "slicer") === "interaction";
}


function collectPageSources(page: TenantPageRecord): PageWidgetSource[] {
  const fromWidgets = sourcesFromWidgetsUnique(page.page_spec.widgets);
  const fromFilters = (page.page_spec.filters ?? [])
    .map((f) => f.source_viz)
    .filter((s): s is PageWidgetSource => !!s);
  const seen = new Set<string>();
  const out: PageWidgetSource[] = [];
  for (const s of [...fromWidgets, ...fromFilters]) {
    const key = widgetSourceKey(s);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}
import {
  dynamicChartFiltersToColumnFilters,
  hasActiveFilterSelections,
  hasActiveDateRangeSelections,
  nextApplyChartDynamicFilter,
  nextClearAllFilterSelections,
  nextClearAllDateRangeSelections,
  nextClearFilterSelection,
  nextClearDateRange,
  nextRemoveDynamicChartFilter,
  nextSelectAllFilterValues,
  nextSetDateRange,
  nextToggleDynamicChartFilter,
  nextToggleFilterValue,
  type DateRangeSelections
} from "../lib/pageFilterInteractions.js";
import {
  isTabsWidget,
  indexBatchResolve,
  isNotAvailableError,
  isPreparingItem,
  PREPARING_POLL_MAX,
  PREPARING_POLL_MS,
  resolvedSourcePort,
  sourcesFromWidgetsUnique,
  widgetSourceKey,
  type PageContentWidget,
  type PageTabsWidget,
  type PageWidget,
  type PageWidgetSource
} from "../lib/pageWidgets.js";
import { reactiveExecuteNode, type ReactiveParamInput } from "../lib/reactiveVisualization.js";
import { apiFetchBlob, triggerBlobDownload } from "../lib/api.js";
import { flattenContentWidgets } from "../lib/pageWidgets.js";
import { refreshPageData, refreshToastMessages } from "../lib/pageRefresh.js";

type Entitlement = "viewer" | "builder" | "admin";

type PageSpecV1 = {
  version: "page.v1";
  layout: {
    cols: 12;
    rowHeight: number;
    gap: number;
    marginX?: number;
    marginY?: number;
    background?: string;
  };
  filters?: PageFilter[];
  widgets: PageWidget[];
};

type TenantPageRecord = {
  id: string;
  title: string;
  route_key: string;
  description: string | null;
  status: "draft" | "published" | "archived";
  page_spec: PageSpecV1;
  is_viewer_landing: boolean;
  updated_at: string;
};

type ResolveItem = {
  source_key: string;
  name?: string;
  source?: PageWidgetSource;
  node_type?: string;
  upstream_ai_provider?: boolean;
  ai_provider_node_id?: string | null;
  citation_sources?: Array<{ ref_id: string; url: string; title: string }>;
  last?: {
    renderModel?: RenderModel;
    runAt?: string | null;
  };
  meta?: {
    asOfRunAt: string | null;
    rowCount?: number | null;
    durationMs?: number | null;
    needsRecompute?: boolean;
    lastError?: string | null;
  };
};

export function TenantPage(props: { entitlement: Entitlement; routeKeyOverride?: string }) {
  const toast = useToast();
  const nav = useNavigate();
  const params = useParams<{ routeKey: string }>();
  const routeKey = props.routeKeyOverride ?? params.routeKey ?? "";
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** ADR-0205 Amendment 3: opened by link, but this person may not use it. */
  const [notAvailable, setNotAvailable] = useState(false);
  const [page, setPage] = useState<TenantPageRecord | null>(null);
  // ADR-0104 — workflows that schedule this report (powers "Schedule this report").
  const [reportSchedules, setReportSchedules] = useState<{ id: string; name: string }[]>([]);
  const [schedulingReport, setSchedulingReport] = useState(false);
  const [itemsById, setItemsById] = useState<Record<string, ResolveItem>>({});
  // Read by the reactive-dispatch effect below, which must not re-fire on
  // every render-model swap it causes itself — so a ref, not a dependency.
  const itemsByIdRef = useRef<Record<string, ResolveItem>>({});
  itemsByIdRef.current = itemsById;
  const [errorsById, setErrorsById] = useState<Record<string, { code: string; message: string }>>({});
  const [filterSelections, setFilterSelections] = useState<Record<string, Set<string>>>({});
  const [dateRangeSelections, setDateRangeSelections] = useState<DateRangeSelections>({});
  /** Per widget: when true, multi-select driver uses complement of selected values for bound filters. */
  const [rowDriveUnselectedByWidget, setRowDriveUnselectedByWidget] = useState<Record<string, boolean>>({});
  const [isolateSelectionByWidget, setIsolateSelectionByWidget] = useState<Record<string, boolean>>({});
  const [sourcesPopoverWidgetId, setSourcesPopoverWidgetId] = useState<string | null>(null);
  /** Active tab id per tab-container widget id (runtime). */
  const [activeTabByTabsWidget, setActiveTabByTabsWidget] = useState<Record<string, string>>({});
  const [exportingByTabsWidgetId, setExportingByTabsWidgetId] = useState<Record<string, boolean>>({});
  const [exportingReport, setExportingReport] = useState(false);
  const [dynamicChartFilters, setDynamicChartFilters] = useState<Array<{
    id: string;
    sourceWidgetId: string;
    sourceColumn: string;
    value: string;
    label: string;
  }>>([]);

  // ── ADR-0056: reactive_visualization dispatch state ────────────────────
  // When a parameter-bound slicer changes, we fire POST /reactive-execute
  // per-node with a 100ms debounce floor and abort any prior in-flight
  // request for the same node. Per-widget "loading" state drives an
  // overlay on the affected widget so the old chart stays visible until
  // the new render model arrives.
  const reactiveAbortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const reactiveDebounceTimersRef = useRef<Map<string, number>>(new Map());
  const [reactiveLoadingWidgetIds, setReactiveLoadingWidgetIds] = useState<Set<string>>(new Set());

  // Abort everything on unmount.
  useEffect(() => {
    return () => {
      for (const ctl of reactiveAbortControllersRef.current.values()) {
        try { ctl.abort(); } catch { /* best-effort */ }
      }
      reactiveAbortControllersRef.current.clear();
      for (const handle of reactiveDebounceTimersRef.current.values()) {
        window.clearTimeout(handle);
      }
      reactiveDebounceTimersRef.current.clear();
    };
  }, []);

  // When filterSelections change, fire a reactive-execute for any widget on
  // the page driven by a kind:"param" binding. Debounced (100ms) and aborted
  // per-widget so multiple slicers bound to the same reactive widget collapse
  // into one request.
  useEffect(() => {
    if (!page) return;
    const filters = page.page_spec.filters ?? [];
    const widgets = page.page_spec.widgets;

    function* walkLeafWidgets(ws: PageWidget[]): Generator<PageContentWidget> {
      for (const w of ws) {
        if (isTabsWidget(w)) {
          for (const tab of (w as PageTabsWidget).tabs) yield* walkLeafWidgets(tab.widgets);
        } else {
          yield w as PageContentWidget;
        }
      }
    }
    const leafWidgets = [...walkLeafWidgets(widgets)];

    // Group kind:"param" bindings by target widget. Bindings for missing
    // widgets, or widgets not bound on render_out, are dropped.
    const byWidgetId = new Map<string, {
      widget: PageContentWidget;
      entries: Array<{ filter: PageFilter; paramName: string }>;
    }>();
    for (const f of filters) {
      for (const b of f.bindings) {
        if (b.kind !== "param") continue;
        if (!b.widget_id || !b.param_name) continue;
        const widget = leafWidgets.find((w) => w.id === b.widget_id);
        if (!widget) continue;
        const resolvedPort = resolvedSourcePort(widget.source, itemsByIdRef.current[widgetSourceKey(widget.source)]);
        if (resolvedPort !== "render_out") continue;
        let entry = byWidgetId.get(widget.id);
        if (!entry) {
          entry = { widget, entries: [] };
          byWidgetId.set(widget.id, entry);
        }
        entry.entries.push({ filter: f, paramName: b.param_name });
      }
    }

    if (byWidgetId.size === 0) return;

    for (const [widgetId, { widget, entries }] of byWidgetId.entries()) {
      const operationId = widget.source.operation_id;
      const nodeId = widget.source.node_id;
      const sourceKey = widgetSourceKey(widget.source);

      // Build params map from the filter selections. Multi-select produces
      // an array; single-select produces a scalar. Empty selection → null
      // (lets the SQL's `$p IS NULL OR ...` idiom keep all rows).
      const params: Record<string, ReactiveParamInput> = {};
      for (const { filter, paramName } of entries) {
        const sel = filterSelections[filter.id];
        const values = sel ? [...sel] : [];
        if (values.length === 0) {
          params[paramName] = null;
        } else if (filter.multi) {
          params[paramName] = values;
        } else {
          params[paramName] = values[0] ?? null;
        }
      }

      // 100ms per-widget debounce.
      const prevTimer = reactiveDebounceTimersRef.current.get(widgetId);
      if (prevTimer !== undefined) window.clearTimeout(prevTimer);

      const timerHandle = window.setTimeout(() => {
        reactiveDebounceTimersRef.current.delete(widgetId);

        // Abort any prior in-flight request for this widget.
        const prevCtl = reactiveAbortControllersRef.current.get(widgetId);
        if (prevCtl) {
          try { prevCtl.abort(); } catch { /* best-effort */ }
        }
        const ctl = new AbortController();
        reactiveAbortControllersRef.current.set(widgetId, ctl);

        setReactiveLoadingWidgetIds((prev) => {
          const next = new Set(prev);
          next.add(widgetId);
          return next;
        });

        void reactiveExecuteNode(operationId, nodeId, params, ctl.signal)
          .then((rm) => {
            if (ctl.signal.aborted) return;
            // Swap the widget's render model in itemsById so re-render uses it.
            setItemsById((prev) => {
              const existing = prev[sourceKey];
              return {
                ...prev,
                [sourceKey]: {
                  ...(existing ?? { source_key: sourceKey }),
                  last: { ...(existing?.last ?? {}), renderModel: rm as RenderModel, runAt: new Date().toISOString() }
                }
              };
            });
          })
          .catch((err: unknown) => {
            // Aborts produce a DOMException with name "AbortError"; silent.
            if (err instanceof DOMException && err.name === "AbortError") return;
            // Surface other errors on the widget with an inline error card
            // via errorsById. Don't toast — the overlay plus the retained
            // prior chart is enough to avoid noisy dead ends.
            const msg = err instanceof Error ? err.message : String(err);
            setErrorsById((prev) => ({
              ...prev,
              [sourceKey]: { code: "reactive_execute_failed", message: msg }
            }));
          })
          .finally(() => {
            // Clear the loading flag only if this is still the latest request
            // — an in-flight newer request will also clear on its own finally.
            if (reactiveAbortControllersRef.current.get(widgetId) === ctl) {
              reactiveAbortControllersRef.current.delete(widgetId);
            }
            setReactiveLoadingWidgetIds((prev) => {
              const next = new Set(prev);
              next.delete(widgetId);
              return next;
            });
          });
      }, 100);

      reactiveDebounceTimersRef.current.set(widgetId, timerHandle);
    }
  }, [filterSelections, page]);

  // The page id is passed in, never read from state: `loadPage` is memoised
  // on the route key, so a `page` read here on first load was the initial
  // null, the request named no page, and a viewer was refused outright.
  const resolveSources = async (
    pageIdToRender: string,
    sources: Array<{ type: "operation_node"; operation_id: string; node_id: string; port_id?: string }>
  ) => {
    const resolved = await apiFetch<{
      items: ResolveItem[];
      errors: Array<{ source_key: string; code: string; message: string }>;
    }>("/api/tenant/visualizations/batch-resolve", {
      method: "POST",
      // ATLAS-SEC-064: name the page being rendered. The hub checks the caller
      // can open it and holds the request to that page's own sources.
      body: { sources, page_id: pageIdToRender }
    });
    // Keyed by the source keys THIS page computes (widgetSourceKey), not only
    // by the hub's — a port-less render widget is keyed on render_out by the
    // hub and on tabular_out here. See indexBatchResolve.
    const indexed = indexBatchResolve(sources, resolved);
    return { nextItems: indexed.items, nextErrors: indexed.errors, raw: resolved };
  };

  const loadPage = useCallback(async () => {
    if (!routeKey) return;
    setLoading(true);
    setError(null);
    setNotAvailable(false);
    try {
      const pageData = await apiFetch<{ page: TenantPageRecord }>(`/api/tenant/pages/by-route/${encodeURIComponent(routeKey)}`);
      const loadedPage = pageData.page;
      setPage(loadedPage);
      const sources = collectPageSources(loadedPage);
      if (sources.length === 0) {
        setItemsById({});
        setErrorsById({});
        return;
      }

      const { nextItems, nextErrors } = await resolveSources(loadedPage.id, sources);

      setItemsById(nextItems);
      setErrorsById(nextErrors);
    } catch (e) {
      if (isNotAvailable(e)) setNotAvailable(true);
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [routeKey]);

  const refreshData = useCallback(async () => {
    if (!page) return;
    setRefreshing(true);
    try {
      const result = await refreshPageData(page.id);
      const sources = collectPageSources(page);
      if (sources.length > 0) {
        const { nextItems, nextErrors } = await resolveSources(page.id, sources);
        setItemsById(nextItems);
        setErrorsById(nextErrors);
      }
      for (const t of refreshToastMessages(result)) toast.push(t);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRefreshing(false);
    }
  }, [page, toast]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  // While any widget's step is being computed in the background (a never-run
  // or stale step the hub healed on open), re-resolve until the row lands.
  // Bounded: after the cap the "preparing" state stays as-is; the user can
  // reload or hit Refresh data.
  const preparingPollCountRef = useRef(0);
  const anyPreparing = useMemo(() => Object.values(itemsById).some(isPreparingItem), [itemsById]);
  useEffect(() => {
    if (!page || !anyPreparing) {
      preparingPollCountRef.current = 0;
      return;
    }
    if (preparingPollCountRef.current >= PREPARING_POLL_MAX) return;
    const handle = window.setTimeout(() => {
      preparingPollCountRef.current += 1;
      const sources = collectPageSources(page);
      if (sources.length === 0) return;
      void resolveSources(page.id, sources)
        .then(({ nextItems, nextErrors }) => {
          setItemsById(nextItems);
          setErrorsById(nextErrors);
        })
        .catch(() => {
          /* keep what is showing; the next tick retries */
        });
    }, PREPARING_POLL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, anyPreparing, itemsById]);


  const dynamicColumnFilters = useMemo(
    () => dynamicChartFiltersToColumnFilters(dynamicChartFilters),
    [dynamicChartFilters]
  );

  const sourceRenderModelByFilterId = useMemo(() => {
    if (!page) return {} as Record<string, RenderModel | undefined>;
    const m: Record<string, RenderModel | undefined> = {};
    for (const f of page.page_spec.filters ?? []) {
      const key = filterSourceKey(f, page.page_spec.widgets);
      m[f.id] = key ? itemsById[key]?.last?.renderModel : undefined;
    }
    return m;
  }, [page, itemsById]);

  const effectiveFilterSelections = useMemo(() => {
    if (!page) return {} as Record<string, Set<string>>;
    const pf = page.page_spec.filters ?? [];
    return effectiveFilterSelectionsForRender(
      pf,
      filterSelections,
      rowDriveUnselectedByWidget,
      sourceRenderModelByFilterId
    );
  }, [page, filterSelections, rowDriveUnselectedByWidget, sourceRenderModelByFilterId]);

  // ADR-0104 — load any schedules that already reference this report. Builders
  // only (viewers never see scheduling controls). Drives the action-bar
  // affordance: none → "Schedule this report"; some → links to the schedule(s).
  // MUST sit above the early returns below — it is an unconditional hook.
  const pageId = page?.id;
  useEffect(() => {
    if (!pageId || props.entitlement === "viewer") {
      setReportSchedules([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiFetch<{ workflows: { id: string; name: string }[] }>(
          `/api/tenant/pages/${encodeURIComponent(pageId)}/schedules`
        );
        if (!cancelled) setReportSchedules(data.workflows ?? []);
      } catch {
        if (!cancelled) setReportSchedules([]);
      }
    })();
    return () => { cancelled = true; };
  }, [pageId, props.entitlement]);

  if (loading) {
    return (
      <PageLayout title="Page">
        <div className="card">
          <LoadingState />
        </div>
      </PageLayout>
    );
  }

  if (notAvailable) {
    return (
      <PageLayout title="Page">
        <NotAvailableState thing="page" />
      </PageLayout>
    );
  }

  if (error || !page) {
    return (
      <PageLayout title="Page">
        <div className="card">
          <ErrorState detail={error ?? "Page not found"} />
        </div>
      </PageLayout>
    );
  }

  const pageFilters = page.page_spec.filters ?? [];
  const hasActiveFilters = hasActiveFilterSelections(filterSelections) || hasActiveDateRangeSelections(dateRangeSelections) || dynamicChartFilters.length > 0;
  const layout = page.page_spec.layout;

  // ADR-0099 whole-page export from the viewer header. Same server-built
  // workbook the tabs container button and subscriptions use, but scoped to
  // the entire page (`kind: "whole_page"`) — the tabs button stays scoped to
  // its own container (`kind: "tabs_widget"`). Published pages only, matching
  // what subscriptions deliver.
  const reportContentWidgets = flattenContentWidgets(page.page_spec.widgets);
  const canExportReport = page.status === "published" && reportContentWidgets.length > 0;
  const handleExportReport = async () => {
    setExportingReport(true);
    try {
      // Collect on-screen widget labels so the workbook tab names match what
      // the user sees on the page (the server doesn't know `itemsById` — it
      // falls back to operation node names without this).
      const widgetTitlesPayload: Record<string, string> = {};
      for (const cw of reportContentWidgets) {
        const key = widgetSourceKey(cw.source);
        const item = itemsById[key];
        if (item?.name?.trim()) widgetTitlesPayload[cw.id] = item.name.trim();
      }
      const { blob, filename, contentType } = await apiFetchBlob(
        `/api/tenant/pages/${encodeURIComponent(page.id)}/export-workbook`,
        {
          body: {
            content_selection: { kind: "whole_page" },
            widget_titles: widgetTitlesPayload,
          },
        },
      );
      triggerBlobDownload(
        new Blob([blob], { type: contentType }),
        filename ?? `${page.title || "export"}.xlsx`,
      );
      toast.push({ kind: "success", message: "Workbook downloaded" });
    } catch (e) {
      toast.push({ kind: "error", message: `Export failed: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setExportingReport(false);
    }
  };

  function renderLeafWidget(widget: PageContentWidget, placement: "root" | "nested") {
    const isRowFilterDriver = pageFilters.some((f) => f.source_widget_id === widget.id && isInteractionFilter(f));
    const wKey = widgetSourceKey(widget.source);
    const item = itemsById[wKey];
    const missing = errorsById[wKey];
    const rawRenderModel = item?.last?.renderModel;
    const renderModel =
      rawRenderModel && (pageFilters.length > 0 || dynamicColumnFilters.length > 0)
        ? filterRenderModel(rawRenderModel, widget.id, pageFilters, effectiveFilterSelections, dynamicColumnFilters, dateRangeSelections)
        : rawRenderModel;
    const title = widget.title_override || item?.name || "Widget";
    const gridStyle =
      placement === "root"
        ? {
            gridColumn: `${widget.grid.x + 1} / span ${widget.grid.w}`,
            gridRow: `${widget.grid.y + 1} / span ${widget.grid.h}`,
            minHeight: layout.rowHeight * widget.grid.h
          }
        : {
            gridColumn: `${widget.grid.x + 1} / span ${widget.grid.w}`,
            gridRow: `${widget.grid.y + 1} / span ${widget.grid.h}`,
            minHeight: layout.rowHeight * widget.grid.h
          };
    const reactiveLoading = reactiveLoadingWidgetIds.has(widget.id);
    return (
      <section key={widget.id} className="card pageWidgetCard" style={gridStyle}>
        <header className="pageWidgetHeader">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <h3 style={{ margin: 0 }}>{title}</h3>
              {item?.upstream_ai_provider ? (
                <span
                  className="pageAiBadge"
                  title={`This widget uses AI-provided data.${item.last?.runAt ? ` Last updated: ${new Date(item.last.runAt).toLocaleString()}.` : ""}`}
                >
                  ✦ AI
                </span>
              ) : null}
              {reactiveLoading ? (
                <span className="pill pillWarn" aria-live="polite" title="Re-executing reactive viz SQL on the server">
                  ⟳ updating…
                </span>
              ) : null}
            </div>
            {item?.meta?.asOfRunAt || item?.meta?.lastError || item?.meta?.needsRecompute ? (
              <div className="muted" style={{ fontSize: 12, marginTop: 2, display: "flex", alignItems: "center", gap: 6 }}>
                {item?.meta?.asOfRunAt ? (
                  <span>As of: {new Date(item.meta.asOfRunAt).toLocaleString()}</span>
                ) : null}
                {item?.meta?.lastError ? (
                  <span
                    title={`This widget's last refresh failed: ${item.meta.lastError}. Showing the last good data.`}
                    aria-label="Last refresh failed; showing the last good data"
                    style={{ color: "var(--danger-text)", cursor: "help" }}
                  >
                    ⚠
                  </span>
                ) : item?.meta?.needsRecompute ? (
                  <span
                    title="Refreshing in the background — showing the last good data as of the time shown."
                    aria-label="Refreshing in the background; showing the last good data as of the time shown"
                    style={{ color: "var(--warning)", cursor: "help" }}
                  >
                    ⚠
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </header>
        <div className="pageWidgetBody" style={{ position: "relative" }}>
          {reactiveLoading ? (
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.45)",
                backdropFilter: "blur(1px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 5,
                pointerEvents: "none",
                fontSize: 13,
                color: "var(--muted)"
              }}
            >
              ⟳ Updating…
            </div>
          ) : null}
          {missing && isNotAvailableError(missing) ? (
            // Per-widget access: the viewer opened the page but may not read
            // this widget's data. The hub's sentence, not console copy.
            <div className="pageWidgetMissing">
              <div>Not available</div>
              <div className="muted">{missing.message}</div>
            </div>
          ) : missing ? (
            <div className="pageWidgetMissing">
              <div>Visualization missing/unavailable</div>
              {/* The hub's batch-resolve messages are plain sentences with
                  display names — never raw source keys or ids. Safe for all
                  roles, so viewers get the explanation too. */}
              {missing.message ? <div className="muted">{missing.message}</div> : null}
            </div>
          ) : null}
          {!missing && item && !renderModel ? (
            <div className="muted">
              {isPreparingItem(item)
                ? "Preparing… computing this widget from cached data"
                : item.meta?.lastError
                  ? `Couldn't prepare this widget: ${item.meta.lastError}`
                  : "Not run yet"}
            </div>
          ) : null}
          {/* ADR-0070 §3.4 — `kind === "echarts" | "kpi" | "tantable"` covers
              every render model shape: legacy output.visualization, the new
              tantable / kpi_card terminal nodes, AND migrated
              reactive_visualization nodes once their first Run populates the
              `render_out` row. Migrated reactive_viz cases render here as
              `kind: "echarts"` because the dispatcher pipes the SQL output
              through compileViz which preserves the original ECharts option
              shape stored in `spec_reactive_visualization.viz.option`. */}
          {!missing && renderModel?.kind === "echarts" ? (
            <Chart
              option={resolveEchartsOption(renderModel)}
              height="100%"
              onPointClick={(point) => applyChartDynamicFilter(widget.id, point)}
            />
          ) : null}
          {!missing && renderModel?.kind === "kpi" ? <KpiCard metrics={renderModel.metrics} style={renderModel.style} /> : null}
          {!missing && renderModel?.kind === "tantable" ? (
            <TanTableViz
              model={renderModel}
              title={widget.title_override?.trim() || item?.name?.trim() || undefined}
              exportSourceRef={{ operationId: widget.source.operation_id, nodeId: widget.source.node_id }}
              rowFilterUnselected={rowDriveUnselectedByWidget[widget.id] === true}
              onRowFilterUnselectedChange={
                isRowFilterDriver && rawRenderModel?.kind === "tantable" && rawRenderModel.tableSpec.rowSelection === "multi"
                  ? (active) =>
                      setRowDriveUnselectedByWidget((prev) => ({
                        ...prev,
                        [widget.id]: active
                      }))
                  : undefined
              }
              isolateSelection={isolateSelectionByWidget[widget.id] === true}
              onIsolateSelectionChange={
                isRowFilterDriver
                  ? (on) =>
                      setIsolateSelectionByWidget((prev) => ({
                        ...prev,
                        [widget.id]: on
                      }))
                  : undefined
              }
              onRowSelect={isRowFilterDriver ? (ev) => handleTanTableRowSync(widget.id, ev) : undefined}
            />
          ) : null}
          {!missing && renderModel?.kind === "ai_content" ? <AiContentRenderer model={renderModel} /> : null}
          {!missing && renderModel?.kind === "ai_production_prompt" ? (
            <AiProductionPromptPanel text={renderModel.text} />
          ) : null}
          {!missing && item?.citation_sources && item.citation_sources.length > 0 ? (
            <div>
              <button
                type="button"
                className="pageAiSourcesBtn"
                onClick={() => setSourcesPopoverWidgetId((id) => (id === widget.id ? null : widget.id))}
              >
                Sources: {item.citation_sources.length} web references
              </button>
              {sourcesPopoverWidgetId === widget.id ? (
                <div className="pageAiSourcesPopover">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {item.citation_sources.map((c, idx) => (
                      <li key={`${c.url}-${idx}`}>
                        <a href={c.url} target="_blank" rel="noopener noreferrer">
                          {c.title?.trim() ? c.title : c.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  function renderTabsRuntimeWidget(widget: PageTabsWidget) {
    const firstTab = widget.tabs[0];
    const activeId = activeTabByTabsWidget[widget.id] ?? firstTab?.id ?? "";
    const activeTab = widget.tabs.find((t) => t.id === activeId) ?? firstTab;
    const exporting = exportingByTabsWidgetId[widget.id] === true;
    // ADR-0099 Option B: the server builds the workbook. Surface the button
    // when the tabs widget has at least one content widget; the hub will
    // emit a placeholder for any tab whose render_out is missing.
    const exportableContentWidgets = flattenContentWidgets(widget.tabs.flatMap((t) => t.widgets));
    const hasExportableTabs = exportableContentWidgets.length > 0;
    const handleExport = async () => {
      if (!page) return;
      setExportingByTabsWidgetId((prev) => ({ ...prev, [widget.id]: true }));
      try {
        // Collect on-screen widget labels so the workbook tab names match
        // what the user sees on the page (the server doesn't know
        // `itemsById` — it falls back to operation node names without this).
        const widgetTitlesPayload: Record<string, string> = {};
        for (const cw of exportableContentWidgets) {
          const key = widgetSourceKey(cw.source);
          const item = itemsById[key];
          if (item?.name?.trim()) widgetTitlesPayload[cw.id] = item.name.trim();
        }
        const { blob, filename, contentType } = await apiFetchBlob(
          `/api/tenant/pages/${encodeURIComponent(page.id)}/export-workbook`,
          {
            body: {
              content_selection: { kind: "tabs_widget", tabs_widget_id: widget.id },
              widget_titles: widgetTitlesPayload,
            },
          },
        );
        triggerBlobDownload(
          new Blob([blob], { type: contentType }),
          filename ?? `${page.title || "export"}.xlsx`,
        );
        toast.push({ kind: "success", message: "Workbook downloaded" });
      } catch (e) {
        toast.push({ kind: "error", message: `Export failed: ${e instanceof Error ? e.message : String(e)}` });
      } finally {
        setExportingByTabsWidgetId((prev) => {
          const next = { ...prev };
          delete next[widget.id];
          return next;
        });
      }
    };
    return (
      <section
        key={widget.id}
        className="card pageWidgetCard pageTabsContainer"
        style={{
          gridColumn: `${widget.grid.x + 1} / span ${widget.grid.w}`,
          gridRow: `${widget.grid.y + 1} / span ${widget.grid.h}`,
          minHeight: layout.rowHeight * widget.grid.h,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <header className="pageWidgetHeader" style={{ flexShrink: 0 }}>
          <h3 style={{ margin: 0 }}>{widget.title_override ?? ""}</h3>
          <button
            type="button"
            className="btn"
            disabled={exporting || !hasExportableTabs}
            onClick={() => void handleExport()}
          >
            {exporting ? "Exporting…" : "Export to Excel"}
          </button>
        </header>
        <div
          className="tabs pageWidgetTabRow"
          role="tablist"
          aria-label={widget.title_override?.trim() ? widget.title_override.trim() : "Tabbed content"}
        >
          {widget.tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab?.id === t.id}
              className={`tab${activeTab?.id === t.id ? " tabActive" : ""}`}
              onClick={() => setActiveTabByTabsWidget((prev) => ({ ...prev, [widget.id]: t.id }))}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div
          className="pageRuntimeTabPanel"
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
            gridAutoRows: `${layout.rowHeight}px`,
            gap: layout.gap,
            padding: layout.gap,
            overflow: "auto"
          }}
        >
          {activeTab ? activeTab.widgets.map((child) => renderPanelRuntimeWidget(child, "nested")) : null}
        </div>
      </section>
    );
  }

  function renderPanelRuntimeWidget(w: PageWidget, placement: "root" | "nested") {
    if (isTabsWidget(w)) return renderTabsRuntimeWidget(w);
    return renderLeafWidget(w, placement);
  }

  function toggleFilterValue(filterId: string, value: string, multi: boolean) {
    setFilterSelections((prev) => nextToggleFilterValue(prev, filterId, value, multi));
  }

  function selectAll(filterId: string, values: string[]) {
    setFilterSelections((prev) => nextSelectAllFilterValues(prev, filterId, values));
  }

  function clearFilter(filterId: string) {
    setFilterSelections((prev) => nextClearFilterSelection(prev, filterId));
    // Any driver widget whose only active filter was this one should drop out of isolate mode.
    const driverIds = new Set(
      pageFilters.filter((f) => f.id === filterId).map((f) => f.source_widget_id).filter(Boolean)
    );
    if (driverIds.size > 0) {
      setIsolateSelectionByWidget((prev) => {
        const next = { ...prev };
        for (const id of driverIds) delete next[id];
        return next;
      });
    }
  }

  function handleTanTableRowSync(sourceWidgetId: string, event: TanTableRowSelectEvent) {
    setFilterSelections((prev) => applyTanTableRowSelectToSelections(prev, pageFilters, sourceWidgetId, event));
    // When the driver's selection goes empty, drop its isolate flag so the table doesn't
    // end up stuck showing 0 rows. Isolate is a modifier on an active selection.
    if (event.selectedRows.length === 0) {
      setIsolateSelectionByWidget((prev) => {
        if (!prev[sourceWidgetId]) return prev;
        const next = { ...prev };
        delete next[sourceWidgetId];
        return next;
      });
    }
  }

  function applyChartDynamicFilter(sourceWidgetId: string, point: { value: string; column?: string }) {
    const hasConfiguredBinding = pageFilters.some(
      (filter) => filter.source_widget_id === sourceWidgetId && filter.bindings.length > 0
    );
    if (hasConfiguredBinding) {
      setFilterSelections((prev) => nextApplyChartDynamicFilter(prev, pageFilters, sourceWidgetId, point.value));
      return;
    }
    const column = point.column;
    if (!column) return;
    setDynamicChartFilters((prev) =>
      nextToggleDynamicChartFilter(prev, {
        sourceWidgetId,
        sourceColumn: column,
        value: point.value,
        label: `${column}: ${point.value}`
      })
    );
  }

  function clearAllFilters() {
    setFilterSelections(nextClearAllFilterSelections());
    setDateRangeSelections(nextClearAllDateRangeSelections());
    setDynamicChartFilters([]);
    // Nothing is selected anywhere — no driver can be meaningfully isolated.
    setIsolateSelectionByWidget({});
  }

  // ADR-0104 — quick-create a schedule for this report and hand off to the
  // schedule canvas to set cadence. Mirrors the watch inspector's
  // createScheduleForThisWatch.
  async function scheduleThisReport() {
    if (!page) return;
    setSchedulingReport(true);
    try {
      // Server creates the workflow scoped to match this report + a page task.
      const res = await apiFetch<{ workflow: { id: string } }>(
        `/api/tenant/pages/${encodeURIComponent(page.id)}/schedule`,
        { method: "POST" }
      );
      nav(`/app/schedule/workflows/${encodeURIComponent(res.workflow.id)}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      setSchedulingReport(false);
    }
  }

  return (
    <PageLayout
      title={page.title}
      actions={
        <>
          <button className="btn" type="button" onClick={() => void refreshData()} disabled={refreshing}>
            {refreshing ? "Refreshing..." : "Refresh data"}
          </button>
          {canExportReport ? (
            <button className="btn" type="button" onClick={() => void handleExportReport()} disabled={exportingReport}>
              {exportingReport ? "Exporting…" : "Export Report"}
            </button>
          ) : null}
          {props.entitlement !== "viewer" ? (
            reportSchedules.length === 0 ? (
              <button className="btn" type="button" onClick={() => void scheduleThisReport()} disabled={schedulingReport}>
                {schedulingReport ? "Scheduling…" : "Schedule this report"}
              </button>
            ) : (
              <span className="muted" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Scheduled:{" "}
                {reportSchedules.map((w, i) => (
                  <span key={w.id}>
                    {i > 0 ? ", " : ""}
                    <Link to={`/app/schedule/workflows/${encodeURIComponent(w.id)}`}>{w.name}</Link>
                  </span>
                ))}
              </span>
            )
          ) : null}
          {props.entitlement !== "viewer" ? (
            <button className="btn" type="button" onClick={() => nav(`/app/build/pages/${encodeURIComponent(page.id)}`)}>
              Edit page
            </button>
          ) : null}
        </>
      }
    >
      {pageFilters.some(isPageSlicerFilter) || dynamicChartFilters.length > 0 ? (
        <div className="pageFilterBar">
          {pageFilters.filter(isPageSlicerFilter).map((filter) => {
            if (isDateRangeSlicer(filter)) {
              const drState = dateRangeSelections[filter.id] ?? { start: filter.default_start ?? null, end: filter.default_end ?? null };
              return (
                <PageFilterDateRange
                  key={filter.id}
                  filter={filter}
                  state={drState}
                  onChange={(state) => setDateRangeSelections((prev) => nextSetDateRange(prev, filter.id, state))}
                  onClear={() => setDateRangeSelections((prev) => nextClearDateRange(prev, filter.id))}
                />
              );
            }
            const unavailable = slicerNotAvailable(filter, page.page_spec.widgets, errorsById);
            const srcKey = filterSourceKey(filter, page.page_spec.widgets);
            const srcRm = srcKey && !unavailable ? itemsById[srcKey]?.last?.renderModel : undefined;
            const options = srcRm ? distinctValues(srcRm, filter.source_column) : [];
            const selected = filterSelections[filter.id] ?? new Set<string>();
            return (
              <PageFilterDropdown
                key={filter.id}
                filter={filter}
                options={options}
                selected={selected}
                unavailableMessage={unavailable?.message}
                onToggle={(val) => toggleFilterValue(filter.id, val, filter.multi)}
                onSelectAll={() => selectAll(filter.id, options)}
                onClear={() => clearFilter(filter.id)}
              />
            );
          })}
          {hasActiveFilters ? (
            <button className="btn pageFilterClearAll" type="button" onClick={clearAllFilters}>Clear all</button>
          ) : null}
          <PageActiveFilterChips
            chips={dynamicChartFilters}
            onRemove={(id) => setDynamicChartFilters((prev) => nextRemoveDynamicChartFilter(prev, id))}
          />
        </div>
      ) : null}

      <div
        className="pageGrid"
        style={{
          gridTemplateColumns: `repeat(${page.page_spec.layout.cols}, minmax(0, 1fr))`,
          gridAutoRows: `${page.page_spec.layout.rowHeight}px`,
          gap: page.page_spec.layout.gap,
          paddingLeft: page.page_spec.layout.marginX ?? 0,
          paddingRight: page.page_spec.layout.marginX ?? 0,
          paddingTop: page.page_spec.layout.marginY ?? 0,
          paddingBottom: page.page_spec.layout.marginY ?? 0
        }}
      >
        {page.page_spec.widgets.map((widget) => renderPanelRuntimeWidget(widget, "root"))}
      </div>
    </PageLayout>
  );
}
